import { useState, useLayoutEffect, RefObject, useCallback } from 'react'
import { throttle } from 'underscore'

export const useIsOverflowing = <T extends HTMLElement>(ref: RefObject<T>, isMultiline = false): boolean => {
    const [isOverflowing, setIsOverflowing] = useState(false)

    const checkOverflow = useCallback(() => {
        const { current } = ref
        if (!current) return

        // One line can only ever overrun sideways; measuring its height reads sub-pixel rounding
        // as overflow and offers a tooltip on text that is perfectly readable.
        const hasOverflow = isMultiline
            ? current.scrollHeight > current.clientHeight
            : current.scrollWidth > current.clientWidth

        setIsOverflowing(hasOverflow)
    }, [isMultiline, ref])

    const throttledCheckOverflow = useCallback(throttle(checkOverflow, 500), [checkOverflow])

    useLayoutEffect(() => {
        throttledCheckOverflow()

        window.addEventListener('resize', throttledCheckOverflow)

        return () => {
            window.removeEventListener('resize', throttledCheckOverflow)
        }
    }, [throttledCheckOverflow])

    return isOverflowing
}
